// Integration with elFinder, an open-source file manager for web, written in JavaScript using jQuery and jQuery UI
// https://github.com/Studio-42/elFinder (license: BSD 3-Clause)

import {
  type TElementSpecifier,
  toHtmlElements,
  parentElementsWithSelf,
  allByTagNameAs,
} from "@de-xima/xima-common-js-dom";
import {
  type TNewable,
  beingInstance,
  firstItem,
  notUndefined,
  parseJsonObject,
  parseRecordValuesAs,
  parseString,
  some,
  notBlank,
  escapeForRegExpLiteral,
  removeEnd,
  parseJsonArrayAs,
  parsingArrayAs,
  parseArrayAs,
  parseError,
} from "@de-xima/xima-common-js-lang";
import $ from "jquery";
import { throttle, trimEnd } from "lodash-es";

import { getContextPath, getLocaleCode, resolveURL } from "../Util.ts";
import { newMonacoElFinderIntegrator } from "./ElFinderEditorMonaco.ts";

const DefaultDisplayInlineRegExp =
  "^(?:(?:image|video|audio)|application/(?:x-mpegURL|dash+xml)|(?:text/plain|application/pdf)$)";

/**
 * Options for the elFinder file manager, see {@link createSimpleElFinder}.
 */
export interface SimpleElFinderOptions {
  /**
   * Base URL for the elFinder resources. Used by elFinder to load its assets. Can also be set via the data
   * attribute `data-el-finder-base-url`.
   */
  baseUrl?: string;
  /**
   * Optional custom info items to add to the file info dialog. Cannot be set via data attributes.
   */
  customInfoItems?: Record<string, elFinder.ElFinderCustomInfoItem>;
  /**
   * Optional event listeners to add to the file manager. Cannot be set via data attributes.
   */
  customHandlers?: elFinder.ElFinderEventHandlers;
  /**
   * Optional context menu settings to use. Can also be set via the data attribute `data-el-finder-context-menu`.
   * In that case, the value must be a JSON string.
   */
  contextMenu?: elFinder.ElFinderContextMenuSettings;
  /**
   * Regular expression to determine which files should be displayed inline via the quick look
   * dialog. Can also be set via the data attribute `data-el-finder-display-inline-regexp`.
   */
  displayInlineRegExp?: RegExp;
  /**
   * When set and not empty, only these extensions are allowed for file names. Must be specified without a leading
   * dot, e.g. `["pdf", "docx"]`. Can also be set via the data attribute `data-el-finder-extensions`.
   */
  extensions?: string[];
  /**
   * Additional localized messages to add to {@link elFinder.ElFinderInstance.messages}. Can also be set via
   * the data attribute `data-el-finder-messages`.
   */
  messages?: Partial<Record<string, string>>;
  /**
   * Theme ID of the default theme to use. Can also be set via the data attribute `data-el-finder-theme`.
   */
  theme?: string;
  /**
   * Actions that should be available in the toolbar at the top of the file manager. 2-dimensional
   * array, for grouping commands. Can also be set via the data attribute `data-el-finder-toolbar`,
   * the value must a JSON string.
   */
  toolbar?: string[][];
  /**
   * List of default entries to hide in the file info dialog. Can also be set via the data attribute
   * `data-el-finder-hide-info-items`.
   */
  hideInfoItems?: string[];
  /**
   * URL to the elFinder connector. Required. Can also be set via the data attribute `data-el-finder-url`.
   */
  url?: string;
}

const DefaultContextMenu: elFinder.ElFinderContextMenuSettings = {
  cwd: [
    "reload",
    "download",
    "|",
    "upload",
    "mkdir",
    "mkfile",
    "paste",
    "|",
    "view",
    "sort",
    "colwidth",
    "|",
    "info",
    "|",
    "fullscreen",
  ],
  files: [
    "getfile",
    "|",
    "open",
    "download",
    "opendir",
    "quicklook",
    "|",
    "upload",
    "mkdir",
    "|",
    "copy",
    "cut",
    "paste",
    "duplicate",
    "extract",
    "|",
    "rm",
    "|",
    "rename",
    "edit",
    "resize",
    "|",
    "info",
  ],
  navbar: [
    "open",
    "download",
    "|",
    "upload",
    "mkdir",
    "|",
    "copy",
    "cut",
    "paste",
    "duplicate",
    "|",
    "rm",
    "rename",
    "|",
    "info",
  ],
};

const DefaultToolbar: string[][] = [
  ["mkdir", "mkfile", "upload", "download"],
  ["copy", "cut", "rm"],
  ["quicklook", "info"],
  ["view", "sort"],
  ["fullscreen", "help"],
  ["search"],
];

function siblingAs<T>(container: HTMLElement, selector: string, type: TNewable<T, never[]>): T | undefined {
  const parent = container.parentElement;
  if (parent == null) {
    return undefined;
  }
  for (let i = 0; i < parent.children.length; i++) {
    const sibling = parent.children[i];
    if (sibling?.matches(selector)) {
      return sibling instanceof type ? sibling : undefined;
    }
  }
  return undefined;
}

/**
 * Prevent form submission when clicking on buttons. Workaround for ElFinder issue:
 *
 * https://github.com/Studio-42/elFinder/issues/3676
 *
 * @param event The click event.
 */
function preventSubmissionOnButtonClick(event: MouseEvent) {
  if (!(event.target instanceof HTMLElement)) {
    return;
  }
  // Was a button clicked? (or any child of a button element)
  if (some(parentElementsWithSelf(event.target), beingInstance(HTMLButtonElement))) {
    event.preventDefault();
  }
}

/**
 * Gets the bytes of the given string, using the UTF-8 encoding, and encodes those bytes as base64.
 * @param value Value to encode.
 * @returns The base64 encoded value.
 */
function encodeBase64ViaUtf8(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join("");
  return btoa(binString);
}

/**
 * Decodes a base64 encoded value and encodes the resulting bytes using the UTF-8 encoding.
 * @param value Value to decode.
 * @returns The decoded text.
 */
function decodeBase64ViaUtf8(value: string): string {
  const binString = atob(value);
  const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0) ?? 0);
  return new TextDecoder().decode(bytes);
}

function guessBaseUrl(): string {
  const pattern = new RegExp(`${escapeForRegExpLiteral(getContextPath())}/resources/elfinder/([^/]+)/`);
  for (const link of allByTagNameAs("link", HTMLLinkElement)) {
    const match = pattern.exec(link.href);
    if (match !== null) {
      return `${getContextPath()}/resources/elfinder/${match[1]}`;
    }
  }
  return `${getContextPath()}/resources/elfinder/1.0.0`;
}

function createElFinderSettings(options: Partial<SimpleElFinderOptions>): elFinder.ElFinderSettings | undefined {
  const url = options.url;
  const lang = getLocaleCode();

  if (url === undefined) {
    console.error("Cannot initialize elFinder, URL not provided.");
    return undefined;
  }

  const elFinderBase = removeEnd(resolveURL(options.baseUrl ?? guessBaseUrl()), "/");
  const helpSource = `${elFinderBase}/js/i18n/help/${lang}.html.js`;

  const allowedFileNames =
    options.extensions !== undefined
      ? new RegExp(`^[^.]+$|\\.(${options.extensions.map((ext) => `${escapeForRegExpLiteral(ext)}`).join("|")})$`)
      : false;

  const elFinderSettings: elFinder.ElFinderSettings = {
    url,
    lang,
    // cdns: createCdns(elFinderBase),
    baseUrl: `${elFinderBase}/`,
    cssAutoLoad: false,
    dragUploadAllow: true,
    noResizeBySelf: true,
    rememberLastDir: true,
    resizable: false,
    useBrowserHistory: false,
    requestType: "post",
    contextmenu: options.contextMenu ?? DefaultContextMenu,
    enableRootRename: false,
    dispInlineRegex: options.displayInlineRegExp?.source ?? DefaultDisplayInlineRegExp,
    ui: ["toolbar", "tree", "path", "stat"],
    handlers: {
      ...options.customHandlers,
      i18Load: (event, fm) => {
        const locale = fm.i18[fm.lang];
        if (!Array.isArray(locale) && typeof locale === "object") {
          Object.assign(locale.messages ?? {}, options.messages ?? {});
          options.customHandlers?.i18Load?.(event, fm);
        }
      },
    },
    uiOptions: {
      toolbar: options.toolbar ?? DefaultToolbar,
      cwd: {
        iconsView: {
          size: 2,
        },
      },
      toolbarExtra: { preferenceInContextmenu: false, showPreferenceButton: "none" },
    },
    validName: allowedFileNames,
    // commands: [
    // ],
    commandsOptions: {
      edit: {
        makeTextMimes: ["text/plain", "text/html", "text/css", "text/javascript", "application/json"],
        confirmUnsavedBeforeClose: true,
        editors: [
          newMonacoElFinderIntegrator(),
          ...["tuiimgedit"].map((id) => elFinder.editors[id]).filter(notUndefined),
        ],
      },
      preference: {},
      quicklook: {},
      help: { helpSource, view: ["about", "shortcuts", "help"] },
      open: {
        selectAction: "edit/quicklook/info",
      },
      info: {
        hideItems: options.hideInfoItems ?? [],
        showHashAlgorisms: ["md5"],
        modal: true,
        allowMinimize: false,
        nullUrlDirLinkSelf: false,
        custom: { ...options.customInfoItems },
      },
    },
  };

  const theme = options.theme;
  if (theme !== undefined && theme !== "none") {
    elFinderSettings.theme = theme;
  }

  return elFinderSettings;
}

/**
 * Reads settings for ElFinder from data attributes of the DOM element. Can be used
 * within XHTML pages to configure the file manager.
 * @param element Element to read settings from.
 * @returns Settings for the file manager.
 */
function settingsFromDom(element: HTMLElement): Partial<SimpleElFinderOptions> {
  const settings: Partial<SimpleElFinderOptions> = {};
  if (element.dataset.elFinderUrl !== undefined) {
    settings.url = element.dataset.elFinderUrl;
  }
  if (element.dataset.elFinderTheme !== undefined) {
    settings.theme = element.dataset.elFinderTheme;
  }
  if (element.dataset.elFinderMessages !== undefined) {
    settings.messages = parseRecordValuesAs(parseJsonObject(element.dataset.elFinderMessages), parseString);
  }
  if (element.dataset.elFinderBaseUrl !== undefined) {
    settings.baseUrl = element.dataset.elFinderBaseUrl;
  }
  if (element.dataset.elFinderExtensions !== undefined) {
    settings.extensions = element.dataset.elFinderExtensions
      .split(",")
      .filter(notBlank)
      .map((x) => x.trim());
  }
  if (element.dataset.elFinderToolbar !== undefined) {
    settings.toolbar = parseJsonArrayAs(element.dataset.elFinderToolbar, parsingArrayAs(parseString));
  }
  if (element.dataset.elFinderDisplayInlineRegExp !== undefined) {
    settings.displayInlineRegExp = new RegExp(element.dataset.elFinderDisplayInlineRegExp);
  }
  if (element.dataset.elFinderContextMenu !== undefined) {
    settings.contextMenu = parseJsonObject(element.dataset.elFinderContextMenu);
  }
  if (element.dataset.elFinderHideInfoItems !== undefined) {
    settings.hideInfoItems = element.dataset.elFinderHideInfoItems.split(",");
  }
  return settings;
}

function createMultipartTransportSend(
  superMethod: elFinder.ElFinderTransport["send"],
): elFinder.ElFinderTransport["send"] {
  return (opts) => {
    const clonedOpts = { ...opts };

    // we already set the requestType to post, but elFinder sometimes still hardcodes "get"
    clonedOpts.type = "post";
    clonedOpts.contentType = false;
    clonedOpts.processData = false;

    // Convert the search params objects to a FormData object
    // elFinder uses jQuery to convert the object to key-value pairs
    if (!(clonedOpts.data instanceof FormData)) {
      const serializedSearchParams = $.param(clonedOpts.data as JQuery.PlainObject, false);
      const searchParams = new URLSearchParams(serializedSearchParams);
      const formData = new FormData();
      for (const param of searchParams.entries()) {
        formData.append(param[0], param[1]);
      }
      clonedOpts.data = formData;
    }

    return superMethod(clonedOpts);
  };
}

/**
 * Initializes the elFinder file manager on the given element.
 * @param element Element to initialize the file manager on.
 * @param settings Settings for the file manager.
 */
export function destroyElFinder(element: TElementSpecifier<HTMLElement>): void {
  const container = firstItem(toHtmlElements(element));
  if (container === undefined) {
    console.warn("Cannot destroy elFinder, element not found.");
    return;
  }
  container.removeEventListener("click", preventSubmissionOnButtonClick, { capture: true });
  const instance = $(container).elfinder("instance");
  if (instance === undefined) {
    console.warn("Cannot destroy elFinder, instance not found.");
    return;
  }
  instance.destroy();
}

/**
 * Initializes the elFinder file manager on the given element. You can configure some options via `data-`
 * attributes on the element, corresponding to the {@link SimpleElFinderOptions} properties. The intended
 * usage is as follows:
 *
 * ```html
 * <h:inputHidden
 *   id="resourceFileManagerFolder"
 *   pt:class="resource-fm-folder"
 *   value="#{formThemeBean.currentFolder}" />
 * <div
 *   id="resourceFileManager"
 *   class="resource-fm"
 *   data-el-finder-base-url="#{request.contextPath}/resources/elfinder/#{bean.elFinderVersion}"
 *   data-el-finder-url="#{bean.connectorUrl}"
 *   data-el-finder-theme="mt/light"
 *   style="height: 100%;">#{i18n['app.loading']}</div>
 * <script>$(() => Formcycle.ElFinder.createSimpleElFinder(".resource-fm"))</script>
 * ```
 * @param element Element to initialize the file manager on.
 * @param settings Settings for the file manager. At least a
 * {@link SimpleElFinderOptions.url URL} must be provided.
 */
export function createSimpleElFinder(element: TElementSpecifier<HTMLElement>, settings?: SimpleElFinderOptions): void {
  const container = firstItem(toHtmlElements(element));
  if (container === undefined) {
    console.error("Cannot initialize elFinder, element not found.");
    return;
  }

  const resolvedOptions = Object.assign({}, settingsFromDom(container), settings ?? {});
  const elFinderSettings = createElFinderSettings(resolvedOptions);

  if (elFinderSettings === undefined) {
    console.error("Cannot initialize elFinder, settings not resolvable.");
    return;
  }

  // When the hidden input field for the currently selected folder is present,
  // set the initially selected folder to that path.
  const hiddenInputFolder = siblingAs(container, ".resource-fm-folder", HTMLInputElement);
  const initialFolder = hiddenInputFolder?.value;
  if (notBlank(initialFolder)) {
    elFinderSettings.startPathHash = initialFolder;
  }

  // Prevent form submission when clicking on buttons
  // Workaround for ElFinder issue:
  // https://github.com/Studio-42/elFinder/issues/3676
  container.removeEventListener("click", preventSubmissionOnButtonClick, { capture: true });
  container.addEventListener("click", preventSubmissionOnButtonClick, { capture: true });

  // @ts-expect-error
  $.fn.elfinderdialog.defaults.width = 465;

  $(container).elfinder(elFinderSettings);
  const instance = $(container).elfinder("instance");
  if (instance === undefined) {
    console.error("Failed to initialize elFinder, instance was not found.");
    return;
  }

  // We need to use multipart/form-data for all requests, as some servlets containers
  // such as tomcat severely limit the size of the request body for
  // application/x-www-form-urlencoded (half a megabyte by default).
  instance.transport.send = createMultipartTransportSend(instance.transport.send);

  // Save the currently selected folder in the hidden input field
  // This way, we can restore the folder after an AJAX update
  const updateHiddenInputFolder: elFinder.ElFinderEventHandlers["open"] = (_, instance) => {
    if (hiddenInputFolder !== undefined) {
      hiddenInputFolder.value = instance.cwd().hash;
    }
  };
  instance.bind("open", updateHiddenInputFolder);

  // Register a resize observer for when the container size changes
  // Update the elFinder upon resize, so that recalculates the layout
  const resizeObserver = new ResizeObserver(throttle(() => $(container).elfinder("instance")?.resize(), 250));
  instance.one("destroy", () => resizeObserver.disconnect());
  instance.one("destroy", () => instance.unbind("open", updateHiddenInputFolder));
  resizeObserver.observe(container);

  // $(container).one("remove", () => instance.destroy());
}

/**
 * Creates a file hash, using the default elFinder algorithm.
 *
 * Note: Despite its name, this is NOT a hash, but rather an encoding.
 *
 * See https://github.com/Studio-42/elFinder/wiki/Client-Server-API-2.1#file-paths-hashes
 * @param volume Volume ID, e.g. "l0".
 * @param path Path to the file, e.g. `foo/bar`.
 * @returns The file hash, e.g. `l0_Zm9vL2Jhcg`.
 */
export function encodeFileHash(volume: string, path: string): string {
  // Encode the path as base64
  const pathB64 = encodeBase64ViaUtf8(path);
  // Replace + with -, / with _, and = with .
  const pathB64Replaced = pathB64.replace(/[+/=]/g, (c) => (c === "+" ? "-" : c === "/" ? "_" : "."));
  // Remove trailing dots
  const pathB64ReplacedTrimmed = trimEnd(pathB64Replaced, ".");
  return `${volume}_${pathB64ReplacedTrimmed}`;
}

/**
 * Decodes a file hash, using the default elFinder algorithm.
 *
 * Note: Despite its name, this is NOT a hash, but rather an encoding.
 *
 * See https://github.com/Studio-42/elFinder/wiki/Client-Server-API-2.1#file-paths-hashes
 * @param hash The file hash to decode, e.g. `l0_Zm9vL2Jhcg`.
 * @returns The decoded volume and path, e.g. `{volume: "l0", path: "foo/bar"}`.
 */
export function decodeFileHash(hash: string): elFinder.ElFinderFileHash {
  // Split into volume and path
  const indexUnderscore = hash.indexOf("_");
  const volume = hash.substring(0, indexUnderscore);
  const encodedPath = hash.substring(indexUnderscore + 1);

  // Replace - with +, _ with /, and . with =
  const encodedPathReplaced = encodedPath.replace(/[-_.]/g, (c) => (c === "-" ? "+" : c === "_" ? "/" : "="));

  // Decode the path from base64
  const path = decodeBase64ViaUtf8(encodedPathReplaced);

  return { volume, path };
}

export function createExecCommandCallback(
  executor: (fm: elFinder.ElFinderInstance, hashes: string[], data: unknown) => Promise<void>,
): elFinder.ElFinderCommandHandlerBase["exec"] {
  return function (hashes, value) {
    const deferred = $.Deferred();
    const promise = executor(this.fm, hashes, value);
    promise.then(
      () => deferred.resolve(),
      (error) => {
        if (typeof error === "string") {
          this.fm.error(error);
        } else if (Array.isArray(error)) {
          this.fm.error(parseArrayAs(error, parseString));
        } else {
          this.fm.error(parseError(error));
        }
        deferred.resolve();
      },
    );
    return deferred;
  };
}

export function showInfoSavedNotification(fm: elFinder.ElFinderInstance, dialog: JQuery): void {
  dialog.find(".elfinder-info-saved").remove();
  const saved = document.createElement("div");
  const savedIcon = document.createElement("span");
  const savedText = document.createElement("span");
  saved.classList.add("elfinder-info-saved");
  savedIcon.classList.add("elfinder-info-saved-icon");
  savedIcon.textContent = "✓";
  savedText.classList.add("elfinder-info-saved-text");
  savedText.textContent = fm.i18n("cmdInfo.saved");
  saved.style.opacity = "0";
  saved.appendChild(savedIcon);
  saved.append(document.createTextNode(" "));
  saved.appendChild(savedText);
  $(saved).appendTo(dialog).animate({ opacity: 1 }, 200).delay(1500).animate({ opacity: 0 }, 400);
}
